/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { MutualFundDividend, MutualFundDividendFormData } from '@/types/mutualFundDividend';
import { captureSentryException } from '@/integrations/sentry/client';
import { convertToAccountCurrency } from '@lib/currencyConversion/accountCurrency.ts';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Fetches paginated dividend payments for a given mutual fund investment account.
 *
 * @param investmentAccountId - The investment account ID to filter dividends by.
 * @param page - 1-indexed page number (default: 1).
 * @param pageSize - Number of records per page (default: 100).
 * @returns Query result containing `{ dividends, totalCount }`.
 */
export function useMutualFundDividends(
  investmentAccountId: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
) {
  return useQuery({
    queryKey: ['mutual-fund-dividends', investmentAccountId, page, pageSize],
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await supabase
        .from('mutual_fund_dividends' as any)
        .select('*', { count: 'exact' })
        .eq('investment_account_id', investmentAccountId)
        .order('payment_date', { ascending: false })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        dividends: (data ?? []) as unknown as MutualFundDividend[],
        totalCount: count ?? 0,
      };
    },
    enabled: !!investmentAccountId,
  });
}

/**
 * Computes the total dividend amount from an array of dividend records.
 * Returns 0 for an empty array.
 *
 * @param dividends - Array of MutualFundDividend records.
 * @returns Sum of all `amount` fields.
 */
export function computeTotalDividends(dividends: MutualFundDividend[]): number {
  return dividends.reduce((sum, d) => sum + d.amount, 0);
}

export type { MutualFundDividend, MutualFundDividendFormData };

// ---------------------------------------------------------------------------
// Write hook
// ---------------------------------------------------------------------------

/**
 * Mutation hook for recording a new mutual fund dividend payment.
 *
 * The record_mutual_fund_dividend RPC records the dividend against the fund and, when a payout
 * account is given, credits it, all in one database transaction. The payout amount is converted
 * into the payout account's currency first; no rates are fetched when the currencies match.
 */
export function useCreateMutualFundDividend() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ formData }: { formData: MutualFundDividendFormData }): Promise<MutualFundDividend> => {
      const creditAmount = formData.payout_account_id != null
        ? (await convertToAccountCurrency(
            user.id, formData.payout_account_id, formData.amount, 0, formData.currency
          )).convertedAmount
        : null;

      const { data, error } = await supabase.rpc('record_mutual_fund_dividend', {
        p_user_id: user.id,
        p_investment_account_id: formData.investment_account_id,
        p_amount: formData.amount,
        p_dividend_per_unit: formData.dividend_per_unit,
        p_currency: formData.currency,
        p_payment_date: formData.payment_date,
        p_payout_account_id: formData.payout_account_id ?? null,
        p_credit_amount: creditAmount,
      });

      if (error) {
        captureSentryException(error);
        toast.error(error.message ?? 'Failed to record dividend payment.');
        throw error;
      }

      return data as unknown as MutualFundDividend;
    },

    onSuccess: (_result, { formData }) => {
      qc.invalidateQueries({
        queryKey: ['mutual-fund-dividends', formData.investment_account_id],
      });
      qc.invalidateQueries({
        queryKey: ['investment-transactions', formData.investment_account_id],
      });
      if (formData.payout_account_id != null) {
        qc.invalidateQueries({ queryKey: ['transactions'] });
        qc.invalidateQueries({ queryKey: ['accounts'] });
      }
      toast.success('Dividend payment recorded successfully.');
    },
  });
}
